import styled from "styled-components";
import { useFormikContext } from "formik";
import { ExpandRow, TwoColumnsBox, Title } from "./FormsStyled";
import { useContext, useState, useEffect, useRef } from "react";
import { capitalize, find } from "lodash";
import { ItemDialogFormContext } from "./item-context/context";
import ItemFormSuggester from "./suggester/ItemFormSuggester";
import ItemFormSuggestionPicker from "./suggester/ItemFormSuggestionPicker";
import { useSelector } from "react-redux";
import { replaceEmptyWithHyphen, validateSelection, valueOrEmpty } from "../../../utils/helpers/text";
import LOGBOOK_LABEL from "../../../utils/constants/logbookLabel";
import COMMON_LOGS_DATA_MODEL from "./../../../utils/constants/commonLogsDataModel";
import ACTION_LOGS_DATA_MODEL from "../../../utils/constants/actionLogsDataModel";

import { loadInstrumentSuggestion } from "./redux/actions";
import { connect } from "react-redux";
import { generateID } from "@digitallab/grid-common-components";
import { Module, formStates, equipmentStatus, formTypes } from "../../../constants";
import { OwcChip, OwcInput, OwcTypography } from "@one/react";
import { CoverSheetMainPageContext } from "../../cover-sheet/cover-sheet-main-page-context/context";

const InstrumentInfoRow = styled(ExpandRow)`
  font-size: 14px;
  line-height: 20px;

  display: grid;
  text-align: center;
  ${(props) => props.error && `color: #CC0033;`}
`;

const EquipmentDetailsDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const EquipmentIdOrSerialNoMessage = styled.span`
  font-size: 12px;
  line-height: 23px;
  font-weight: 400;
  margin-top: -20px;
`;

const TypeStyles = styled.span`
  font-weight: 500;
`;

const ChipBox = styled.div`
  display: flex;
  flex-direction: column;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  flex-wrap: wrap;
  align-items: center;
  > * {
    margin: 0 4px;
  }

  > :last-child {
    margin: 0;
  }
`;

const InstrumentInfoAndChips = styled(ExpandRow)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  ${ChipBox} {
    flex: 0 1 auto;
  }
`;

const EquipmentStatusChip = ({ status }) => {
  const statusColorMap = {
    [equipmentStatus?.active?.key]: "var(--one-color-interaction-default-positive-1)",
    [equipmentStatus?.pending?.key]: "var(--one-color-interaction-default-warning-1)"
  };
  const color = statusColorMap[status];
  if (!color) {
    return null;
  }
  return (
    <OwcTypography variant="badge" style={{ color }}>
      {capitalize(status)}
    </OwcTypography>
  );
};

const EquipmentDetailsSection = ({ equipmentSuggDetail, formState, showChipBox, gxpReadys }) => {
  if (!equipmentSuggDetail) {
    return null;
  }
  return (
    <InstrumentInfoAndChips>
      <EquipmentDetailsDiv>
        {formState !== formStates.NEW_FORM && (
          <>
            <InstrumentInfoRow data-testid="instrument-identification-equipment-model-label">
              {COMMON_LOGS_DATA_MODEL.equipmentId.value}:{" "}
              <TypeStyles data-testid="instrument-identification-equipment-model-value">
                {valueOrEmpty(equipmentSuggDetail?.equipmentId, false, "-")}
              </TypeStyles>
            </InstrumentInfoRow>
            <InstrumentInfoRow data-testid="instrument-identification-equipment-model-label">
              {COMMON_LOGS_DATA_MODEL.serialNumber.value}:{" "}
              <TypeStyles data-testid="instrument-identification-equipment-model-value">
                {valueOrEmpty(equipmentSuggDetail?.serialNumber, false, "-")}
              </TypeStyles>
            </InstrumentInfoRow>
          </>
        )}
        <InstrumentInfoRow data-testid="instrument-identification-equipment-model-label">
          {COMMON_LOGS_DATA_MODEL.equipmentModel.value}:{" "}
          <TypeStyles data-testid="instrument-identification-equipment-model-value">
            {valueOrEmpty(equipmentSuggDetail?.equipmentModel, false, "-")}
          </TypeStyles>
        </InstrumentInfoRow>
        <InstrumentInfoRow data-testid="instrument-identification-equipment-nickName-label">
          {COMMON_LOGS_DATA_MODEL.equipmentNickName.value}:{" "}
          <TypeStyles data-testid="instrument-identification-equipment-nickName-value">
            {valueOrEmpty(equipmentSuggDetail?.equipmentNickName, false, "-")}
          </TypeStyles>
        </InstrumentInfoRow>
        {showChipBox && <EquipmentChipBox equipmentSuggDetail={equipmentSuggDetail} gxpReadys={gxpReadys} />}
      </EquipmentDetailsDiv>
    </InstrumentInfoAndChips>
  );
};

const EquipmentChipBox = ({ equipmentSuggDetail, gxpReadys }) => (
  <>
    <ChipBox data-testid="run-data-chip-box">
      <div data-testid="run-data-chip-box-col-label-for-gxp">{ACTION_LOGS_DATA_MODEL.gxpReady.value}:</div>
      <OwcChip outlined data-testid="run-data-chip-box-col-value-for-gxp">
        <OwcTypography variant="badge">
          {equipmentSuggDetail?.qualificationStatus
            ? replaceEmptyWithHyphen(
                validateSelection(gxpReadys, {
                  key: equipmentSuggDetail?.qualificationStatus
                })
              )
            : "-"}
        </OwcTypography>
      </OwcChip>
    </ChipBox>
    <ChipBox>
      <div data-testid="run-data-chip-box-col-label-for-system-status">
        {ACTION_LOGS_DATA_MODEL.systemStatus.value}:
      </div>
      <OwcChip outlined data-testid="run-data-chip-box-col-value-for-system-status">
        <OwcTypography variant="badge">
          {equipmentSuggDetail?.equipSystemStatus || equipmentSuggDetail?.systemStatus?.value || "-"}
        </OwcTypography>
      </OwcChip>
    </ChipBox>
    <ChipBox
      style={{
        marginRight: "3rem"
      }}
    >
      <div data-testid="run-data-chip-box-col-label-for-system-status">{ACTION_LOGS_DATA_MODEL.status.value}:</div>
      <OwcChip outlined data-testid="run-data-chip-box-col-value-for-system-status">
        <EquipmentStatusChip status={equipmentSuggDetail?.status} />
      </OwcChip>
    </ChipBox>
  </>
);

const InstrumentIdentificationBox = ({
  clearSuggestionFn,
  selectSuggestionFn,
  showChipBox,
  loadInstrumentSuggestion,
  equipmentDetail,
  type
}) => {
  const formik = useFormikContext();
  const instrumentSuggestion = useSelector((state) => state.runLogsForm.instrumentSuggestion);
  const instrumentSuggestions = useSelector((state) => state.runLogsForm.instrumentSuggestions);
  const gxpReadys = useSelector((store) => store.runLogsForm.gxpReadys);
  const systemStatuses = useSelector((store) => store.runLogsForm.systemStatuses);
  const { isInventoryId, setEquipmentActionObject } = useContext(ItemDialogFormContext);
  const { formState } = useContext(CoverSheetMainPageContext);
  const [focusedOn, setFocusedOn] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [equipmentSuggDetail, setEquipmentSuggDetail] = useState(null);
  const [suggestionSearchDone, setSuggestionSearchDone] = useState(false);
  const instrumentSuggestionsRef = useRef(instrumentSuggestions);
  const user = useSelector((state) => state.user);
  const handleFocusFactory = (on) => (event) => {
    setFocusedOn(on);
    setAnchorEl(event.target);
  };
  const handleBlur = () => {
    setFocusedOn(null);
    setAnchorEl(null);
  };
  const handleInputKeyDown = () => {
    formik.setFieldValue("inventoryId", "", true);
    setSuggestionSearchDone(false);
    if (instrumentSuggestion) {
      clearSuggestionFn({
        loadSuggestion: loadInstrumentSuggestion,
        formik,
        user,
        equipmentDetail
      });
    }
  };
  useEffect(() => {
    if (instrumentSuggestions !== instrumentSuggestionsRef.current) {
      instrumentSuggestionsRef.current = instrumentSuggestions;
      setSuggestionSearchDone(true);
    }
  }, [instrumentSuggestions]);

  useEffect(() => {
    if (isInventoryId && equipmentDetail) {
      selectSuggestionFn(equipmentDetail, {
        loadInstrumentSuggestion,
        formik,
        user
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const detail = instrumentSuggestion || equipmentDetail || null;
    const gxpReady = detail?.qualificationStatus;
    const systemStatus = detail?.equipSystemStatus;
    if (instrumentSuggestion) {
      formik.setFieldValue("inventoryId", instrumentSuggestion?.inventoryId, true);
    }
    setEquipmentSuggDetail(() => detail);
    setEquipmentActionObject(instrumentSuggestion || (equipmentDetail ? instrumentSuggestion : null));
    if (type === formTypes.RUN_LOG) {
      formik.setFieldValue(
        "gxpReady",
        find(gxpReadys, {
          key: gxpReady
        }) || {
          key: "-",
          value: "-"
        }
      );

      formik.setFieldValue(
        "systemStatus",
        find(systemStatuses, {
          value: systemStatus
        }) || {
          key: "-",
          value: "-"
        }
      );
    } else if (type === formTypes.ACTION_LOG && detail) {
      const matchedGxp = find(gxpReadys, { key: gxpReady });
      formik.setFieldValue("gxpReady", {
        newValue: matchedGxp || { key: "", value: "" },
        shouldBePublished: false
      });

      const matchedSystem =
        find(systemStatuses, { value: systemStatus }) || find(systemStatuses, { key: systemStatus });
      formik.setFieldValue("systemStatus", {
        newValue: matchedSystem || { key: "", value: "" },
        shouldBePublished: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentSuggestion, equipmentDetail]);
  const hasEquipmentInput = formik.values.equipmentId || formik.values.serialNumber;
  return (
    <TwoColumnsBox>
      <Title data-testid="instrument-identification-title">{LOGBOOK_LABEL.eqipment_identification_heading}</Title>
      {formState === formStates.NEW_FORM && (
        <>
          <OwcInput
            style={{ width: "100%" }}
            onBlur={(event) => {
              handleBlur(event);
              formik.handleBlur(event);
            }}
            onFocus={handleFocusFactory("equipmentId")}
            data-testid="instrument-identification-equipmentId-input"
            color="primary"
            variant="filled"
            id={generateID.textBoxID(Module.POPUP_FIELDS.equipmentId, "textbox")}
            name="equipmentId"
            label={COMMON_LOGS_DATA_MODEL.equipmentId.value}
            value={valueOrEmpty(formik.values.equipmentId, false, "")}
            onInputChange={(event) => {
              formik.handleChange(event);
            }}
            onInputKeyDown={handleInputKeyDown}
          />
          <OwcInput
            style={{ width: "100%" }}
            onBlur={(event) => {
              handleBlur(event);
              formik.handleBlur(event);
            }}
            onFocus={handleFocusFactory("serialNumber")}
            data-testid="instrument-identification-serial-number-input"
            color="primary"
            variant="filled"
            id={generateID.textBoxID(Module.POPUP_FIELDS.serialNumber, "textbox")}
            name="serialNumber"
            label={COMMON_LOGS_DATA_MODEL.serialNumber.value}
            value={valueOrEmpty(formik.values.serialNumber, false, "")}
            onInputChange={(event) => {
              formik.handleChange(event);
            }}
            onInputKeyDown={handleInputKeyDown}
          />
          <EquipmentIdOrSerialNoMessage>{LOGBOOK_LABEL.equipmentId_serialNo_message}</EquipmentIdOrSerialNoMessage>
        </>
      )}
      <EquipmentDetailsSection
        equipmentSuggDetail={equipmentSuggDetail}
        formState={formState}
        showChipBox={showChipBox}
        gxpReadys={gxpReadys}
      />
      {suggestionSearchDone && !equipmentSuggDetail && hasEquipmentInput ? (
        <InstrumentInfoRow data-testid="instrument-identification-error" error>
          {LOGBOOK_LABEL.equipmentId_serialNo_error_msg}
          {capitalize(user?.site || "")}
          {LOGBOOK_LABEL.equipmentId_serialNo_error_msg_1}
          {capitalize(user?.site || "")}
          {LOGBOOK_LABEL.equipmentId_serialNo_error_msg_2}
        </InstrumentInfoRow>
      ) : null}

      <ItemFormSuggester
        clearSuggestionFn={clearSuggestionFn}
        focusedOn={focusedOn}
        selectSuggestionFn={selectSuggestionFn}
        equipmentDetail={equipmentDetail}
      />
      {!isInventoryId && (
        <ItemFormSuggestionPicker
          anchorEl={anchorEl}
          selectSuggestionFn={selectSuggestionFn}
          PopoverStyle={{ maxHeight: "50%" }}
        />
      )}
    </TwoColumnsBox>
  );
};

export default connect(null, { loadInstrumentSuggestion })(InstrumentIdentificationBox);
